import { useCallback } from 'react';
import { useAuthStore } from '@/store/useAuthStore';
import { usePermission } from '@/hooks/usePermission';
import type { RoleItemTree } from '@/types/action-plan/DuAnView';
import { isDisplayMucTieuRecord } from '@/components/common/ActionPlanV2/core/mucTieu';

/**
 * Check if user can edit/add/delete a project or task (quyền thao tác tổng — buoc9 / admin).
 * Quyền trên từng node cây dùng roleItemTree từ BE (GanDataDuAnCongViec → GetRoleItem).
 */
interface CongViecPermission {
  nguoiPhuTrachID?: string | null;
  nguoiTao?: string | null;
}

export type RoleItemTreeLike = Partial<RoleItemTree> | null | undefined;

export interface ActionPlanRecordPermissionLike {
  congViec?: CongViecPermission | null;
  roleItemTree?: RoleItemTreeLike;
}

export const isItemPhuTrach = (role?: RoleItemTreeLike): boolean => Boolean(role?.isPhuTrach || role?.isEdit);

/**
 * Quyền sửa/lưu chi tiết công việc trong modal — khớp ChiTiet.cshtml (nút Lưu):
 * người tạo, hoặc phụ trách khi stateApproved === 1; admin/fullcontrol luôn được sửa.
 */
export function canEditCongViecChiTiet(params: {
  roleItemTree?: RoleItemTreeLike;
  stateApproved?: number;
  nguoiTaoID?: string | null;
  userID?: string;
  checkFullConTroll?: boolean;
}): boolean {
  const { roleItemTree, stateApproved = 1, nguoiTaoID, userID, checkFullConTroll } = params;
  if (!userID) return false;
  if (checkFullConTroll) return true;

  const isNguoiTao = Boolean(nguoiTaoID && String(nguoiTaoID) === String(userID));
  const isPhuTrach = Boolean(roleItemTree?.isPhuTrach);

  if (isNguoiTao) return true;
  if (isPhuTrach && stateApproved === 1) return true;

  return false;
}

/** Công việc tạm (id/key chứa temp) — không áp roleItemTree, chỉ quyền tổng khi cần. */
export function isTempTableRecord(
  record?: (ActionPlanRecordPermissionLike & { key?: string | number; temp?: boolean; idTemp?: string; isPlaceholder?: boolean }) | null
): boolean {
  if (!record || record.isPlaceholder) return false;
  if (record.temp === true) return true;
  const id = String((record.congViec as { id?: string } | undefined)?.id ?? record.key ?? '');
  const idTemp = String(record.idTemp ?? '');
  const key = String(record.key ?? '');
  return id.startsWith('temp') || idTemp.startsWith('temp') || key.startsWith('temp');
}

/** Chuẩn hóa payload quyền từ tree node, congViec hoặc DuAnDetailView. */
export function resolveActionPlanPermissionRecord(
  input?: ActionPlanRecordPermissionLike | CongViecPermission | { duAnCongViec?: { congViec?: CongViecPermission }; roleItemTree?: RoleItemTreeLike } | null
): ActionPlanRecordPermissionLike {
  if (!input) return {};
  const anyInput = input as {
    duAnCongViec?: { congViec?: CongViecPermission };
    congViec?: CongViecPermission;
    roleItemTree?: RoleItemTreeLike;
  };
  if (anyInput.duAnCongViec) {
    return {
      congViec: anyInput.duAnCongViec.congViec,
      roleItemTree: anyInput.roleItemTree,
    };
  }
  if (anyInput.roleItemTree != null || anyInput.congViec?.nguoiPhuTrachID != null) {
    return {
      congViec: anyInput.congViec ?? (anyInput as CongViecPermission),
      roleItemTree: anyInput.roleItemTree,
    };
  }
  return { congViec: anyInput as CongViecPermission };
}

export const useActionPlanPermission = () => {
  const { profile } = useAuthStore();
  const permissions = usePermission('buoc9');
  const quanLyActionPlan = usePermission('buoc8quanly');

  const isAdmin = Boolean(profile?.isAdmin);
  const currentUserId = profile?.id;

  /** Quyền sửa tổng (module buoc9 update / admin). */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const canEdit = useCallback((congViec?: CongViecPermission | null): boolean => {
    if (isAdmin) return true;
    return Boolean(permissions?.update || permissions?.fullcontrol);
  }, [isAdmin, permissions]);

  const canCreate = useCallback((): boolean => {
    if (isAdmin) return true;
    return permissions?.create ?? false;
  }, [isAdmin, permissions]);

  const canDelete = useCallback((congViec?: CongViecPermission | null): boolean => {
    if (isAdmin) return true;
    if (!congViec || !currentUserId) return false;
    return Boolean(permissions?.delete || permissions?.fullcontrol);
  }, [isAdmin, permissions, currentUserId]);

  const canImport = useCallback((): boolean => {
    if (isAdmin) return true;
    return permissions?.import ?? false;
  }, [isAdmin, permissions]);

  const canExport = useCallback((): boolean => {
    if (isAdmin) return true;
    return permissions?.export ?? false;
  }, [isAdmin, permissions]);

  const canQuanLyActionPlan = useCallback((): boolean => {
    if (isAdmin) return true;
    return quanLyActionPlan?.quanLy ?? false;
  }, [isAdmin, quanLyActionPlan]);

  return {
    canEdit,
    canCreate,
    canDelete,
    canImport,
    canExport,
    isAdmin,
    canQuanLyActionPlan,
  };
};

/**
 * Kết hợp quyền tổng (buoc9) với roleItemTree trên từng công việc/dự án
 * (roleItemTree do BE GanDataDuAnCongViec → GetRoleItem gán).
 */
export const useActionPlanRecordPermission = () => {
  const global = useActionPlanPermission();
  const { isAdmin, canEdit: globalCanEdit, canDelete: globalCanDelete, canCreate: globalCanCreate } = global;

  const canOpenRowContextMenu = useCallback(
    (record: ActionPlanRecordPermissionLike) => {
      if (isTempTableRecord(record)) return true;
      if (isAdmin) return true;
      return isItemPhuTrach(record?.roleItemTree);
    },
    [isAdmin]
  );

  const getRowDisabledForRecord = useCallback(
    (
      record: ActionPlanRecordPermissionLike,
      opts?: { overviewLimitedEdit?: boolean; globalCanEditFn?: (cv?: CongViecPermission | null) => boolean }
    ) => {
      if (isDisplayMucTieuRecord(record)) return false;
      if (opts?.overviewLimitedEdit) return false;
      if (isTempTableRecord(record)) return false;
      const editFn = opts?.globalCanEditFn ?? globalCanEdit;
      if (isAdmin) return !editFn(record?.congViec);
      if (isItemPhuTrach(record?.roleItemTree)) return false;
      return true;
    },
    [isAdmin, globalCanEdit]
  );

  const canEditRecord = useCallback(
    (record: ActionPlanRecordPermissionLike) => {
      if (isDisplayMucTieuRecord(record)) return false;
      if (isTempTableRecord(record)) return globalCanEdit(record?.congViec) || globalCanCreate();
      if (isAdmin) return globalCanEdit(record?.congViec);
      if (isItemPhuTrach(record?.roleItemTree)) return true;
      return false;
    },
    [isAdmin, globalCanEdit, globalCanCreate]
  );

  const canDeleteRecord = useCallback(
    (record: ActionPlanRecordPermissionLike) => {
      if (isDisplayMucTieuRecord(record)) return false;
      if (isTempTableRecord(record)) return true;
      if (isAdmin) return globalCanDelete(record?.congViec);
      if (isItemPhuTrach(record?.roleItemTree)) return true;
      return false;
    },
    [isAdmin, globalCanDelete]
  );

  /** Tạo công việc (root / dự án): buoc9 create hoặc isPhuTrach trên node dự án. */
  const canCreateRecord = useCallback(
    (record?: ActionPlanRecordPermissionLike | null) => {
      if (isAdmin) return true;
      if (record && isItemPhuTrach(record?.roleItemTree)) return true;
      return globalCanCreate();
    },
    [isAdmin, globalCanCreate]
  );

  /** Thêm công việc con: isPhuTrach hoặc admin (không bắt buoc9 create). */
  const canAddSubRecord = useCallback(
    (record: ActionPlanRecordPermissionLike) => {
      if (isDisplayMucTieuRecord(record)) return false;
      if (isTempTableRecord(record)) return globalCanCreate();
      if (isAdmin) return true;
      return isItemPhuTrach(record?.roleItemTree);
    },
    [isAdmin, globalCanCreate]
  );

  return {
    ...global,
    canOpenRowContextMenu,
    getRowDisabledForRecord,
    canEditRecord,
    canDeleteRecord,
    canCreateRecord,
    canAddSubRecord,
  };
};
